const US_PHONE_DIGIT_LENGTH = 10
const US_COUNTRY_CODE = '1'
const MAX_E164_DIGITS = 15
const NANP_PATTERN = /^[2-9][0-8]\d[2-9]\d{2}\d{4}$/

const MIN_E164_DIGITS = 8

/**
 * Whether this value is deliberately outside the North American plan.
 *
 * Only an explicit `+` counts. A bare 10-digit string is a US number being
 * typed, not an international one missing its prefix, so the US path keeps it.
 */
export function isInternationalPhone(raw: string | null | undefined): boolean {
  const trimmed = String(raw ?? '').trim()
  if (!trimmed.startsWith('+')) return false
  const digits = trimmed.replace(/\D/g, '')
  return !(
    digits.startsWith(US_COUNTRY_CODE) &&
    digits.length <= US_PHONE_DIGIT_LENGTH + 1
  )
}

/**
 * What a phone input should hold as the user types.
 *
 * US numbers stay bare digits so the existing mask keeps working. Anything
 * international keeps its `+` and every digit -- truncating to ten silently
 * turned +998947577542 into 9989475775, which is a different, valid-looking,
 * wrong number.
 */
export function phoneInputValue(raw: string | null | undefined): string {
  if (!raw) return ''
  if (!isInternationalPhone(raw)) return usPhoneDigits(raw)
  return `+${String(raw).replace(/\D/g, '').slice(0, MAX_E164_DIGITS)}`
}

/** How that value should read back to the user while typing. */
export function formatPhoneInput(raw: string | null | undefined): string {
  if (!raw) return ''
  return isInternationalPhone(raw) ? phoneInputValue(raw) : formatUsPhone(raw)
}

/** Valid as a US number, or as any E.164 number. */
export function isValidPhone(raw: string | null | undefined): boolean {
  if (!raw) return false
  if (!isInternationalPhone(raw)) return isValidUsPhone(raw)
  const digits = String(raw).replace(/\D/g, '')
  return digits.length >= MIN_E164_DIGITS && digits.length <= MAX_E164_DIGITS
}

/** E.164 for storage, for US and international alike. */
export function phoneToE164(raw: string | null | undefined): string | null {
  if (!raw) return null
  if (!isInternationalPhone(raw)) return usPhoneToE164(raw)
  const digits = String(raw).replace(/\D/g, '')
  if (digits.length < MIN_E164_DIGITS || digits.length > MAX_E164_DIGITS) return null
  return `+${digits}`
}

export function usPhoneDigits(raw: string | null | undefined): string {
  if (!raw) return ''
  const stripped = String(raw).replace(/\D/g, '')
  const withoutCountryCode =
    stripped.length === US_PHONE_DIGIT_LENGTH + 1 &&
    stripped.startsWith(US_COUNTRY_CODE)
      ? stripped.slice(1)
      : stripped
  return withoutCountryCode.slice(0, US_PHONE_DIGIT_LENGTH)
}

export function isValidUsPhone(raw: string | null | undefined): boolean {
  return NANP_PATTERN.test(usPhoneDigits(raw))
}

export function usPhoneToE164(raw: string | null | undefined): string | null {
  const digits = usPhoneDigits(raw)
  if (!NANP_PATTERN.test(digits)) return null
  return `+${US_COUNTRY_CODE}${digits}`
}

export function formatUsPhone(raw: string | null | undefined): string {
  const digits = usPhoneDigits(raw)
  if (digits.length === 0) return ''
  if (digits.length < 4) return `(${digits}`
  if (digits.length < 7) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
}

export function formatPhone(raw: string | null | undefined): string {
  if (!raw) return ''
  const hasCountryCode = String(raw).trim().startsWith('+')
  const digits = String(raw).replace(/\D/g, '')
  if (digits.length === 0) return ''
  if (!hasCountryCode && digits.length <= US_PHONE_DIGIT_LENGTH) {
    return formatUsPhone(digits)
  }
  if (
    digits.length === US_PHONE_DIGIT_LENGTH + 1 &&
    digits.startsWith(US_COUNTRY_CODE)
  ) {
    return formatUsPhone(digits.slice(1))
  }
  if (digits.length > US_PHONE_DIGIT_LENGTH) {
    // Splitting the last ten digits off and bracketing them is a US habit that
    // does not travel: it rendered +998944137300 as "+99 (894) 413-7300",
    // inventing a country code and a grouping neither of which exist. Without a
    // per-country grouping table, plain E.164 is the honest answer.
    return `+${digits}`
  }
  return `+${digits}`
}

const PHONE_IN_TEXT_PATTERN = /\+?\d[\d\s().-]{8,}\d/g

/**
 * Format any phone numbers that appear inside a larger string (e.g. a
 * notification title like "SMS from +18015852905"), leaving the rest intact.
 */
export function formatPhonesInText(text: string | null | undefined): string {
  if (!text) return ''
  return text.replace(PHONE_IN_TEXT_PATTERN, (match) => {
    const digits = match.replace(/\D/g, '')
    if (
      digits.length < US_PHONE_DIGIT_LENGTH ||
      digits.length > MAX_E164_DIGITS
    )
      return match
    return formatPhone(match)
  })
}

export function phoneToDialable(raw: string | null | undefined): string | null {
  if (!raw) return null
  const hasCountryCode = String(raw).trim().startsWith('+')
  const digits = String(raw).replace(/\D/g, '')
  if (digits.length === 0) return null
  if (!hasCountryCode && digits.length === US_PHONE_DIGIT_LENGTH) {
    return `+${US_COUNTRY_CODE}${digits}`
  }
  return `+${digits}`
}
